import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Wrench,
  CheckCircle2,
  AlertCircle,
  Eye,
  Save,
  Clock,
  Sparkles,
  ShieldCheck,
  RefreshCw,
  Power,
  X,
} from 'lucide-react';
import { CoupleSettings, WebMaintenanceSettings } from '../../types';
import { WebMaintenanceScreen } from '../WebMaintenanceScreen';
import { ConfirmationModal } from '../ConfirmationModal';
import {
  updateWebMaintenanceStatusInDb,
  saveWebMaintenanceConfigInDb,
} from '../../services/firebaseDb';

interface WebSettingsAdminSectionProps {
  settings: CoupleSettings;
  activeProfile: 'novio' | 'novia' | 'admin';
  onSaveSettings: (settings: CoupleSettings) => void;
  onNavigateToPublic: (route?: string) => void;
}

export const WebSettingsAdminSection: React.FC<WebSettingsAdminSectionProps> = ({
  settings,
  activeProfile,
  onSaveSettings,
}) => {
  // Read authoritative maintenance state from settings (live Firestore document)
  const isMaintenanceActive = Boolean(
    settings.webMaintenance?.isEnabled ??
    settings.webMaintenance?.maintenanceEnabled ??
    (settings as any).maintenanceEnabled
  );

  const initialTitle =
    settings.webMaintenance?.title ||
    settings.webMaintenance?.maintenanceTitle ||
    settings.maintenanceTitle ||
    'Estamos preparando algo bonito';

  const initialMessage =
    settings.webMaintenance?.message ||
    settings.webMaintenance?.maintenanceMessage ||
    settings.maintenanceMessage ||
    'Estamos realizando algunas mejoras para ofrecerte una mejor experiencia.';

  const initialReturn =
    settings.webMaintenance?.estimatedReturn ||
    settings.webMaintenance?.maintenanceReturnTime ||
    settings.maintenanceReturnTime ||
    'Volveremos muy pronto';

  // Form state for customization
  const [title, setTitle] = useState(initialTitle);
  const [message, setMessage] = useState(initialMessage);
  const [returnTime, setReturnTime] = useState(initialReturn);

  // Sync form with incoming real-time Firebase changes
  useEffect(() => {
    if (settings.webMaintenance?.title || settings.webMaintenance?.maintenanceTitle) {
      setTitle(settings.webMaintenance.title || settings.webMaintenance.maintenanceTitle || '');
    }
    if (settings.webMaintenance?.message || settings.webMaintenance?.maintenanceMessage) {
      setMessage(settings.webMaintenance.message || settings.webMaintenance.maintenanceMessage || '');
    }
    if (settings.webMaintenance?.estimatedReturn || settings.webMaintenance?.maintenanceReturnTime) {
      setReturnTime(settings.webMaintenance.estimatedReturn || settings.webMaintenance.maintenanceReturnTime || '');
    }
  }, [
    settings.webMaintenance?.title,
    settings.webMaintenance?.maintenanceTitle,
    settings.webMaintenance?.message,
    settings.webMaintenance?.maintenanceMessage,
    settings.webMaintenance?.estimatedReturn,
    settings.webMaintenance?.maintenanceReturnTime,
  ]);

  // Action states
  const [isToggling, setIsToggling] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Modals
  const [confirmModalType, setConfirmModalType] = useState<'activate' | 'deactivate' | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Format date helper
  const formattedUpdateDate = () => {
    const rawDate =
      settings.webMaintenance?.maintenanceUpdatedAt ||
      settings.webMaintenance?.updatedAt ||
      settings.webMaintenance?.enabledAt ||
      settings.maintenanceUpdatedAt;

    if (!rawDate) return null;

    try {
      let dateObj: Date;
      // Handle Firebase Timestamp or ISO string
      if (rawDate && typeof rawDate.toDate === 'function') {
        dateObj = rawDate.toDate();
      } else {
        dateObj = new Date(rawDate);
      }

      if (isNaN(dateObj.getTime())) return null;

      return dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  const updatedByText = () => {
    const uid =
      settings.webMaintenance?.maintenanceUpdatedBy ||
      settings.webMaintenance?.enabledBy ||
      settings.maintenanceUpdatedBy;

    if (!uid) return 'Administrador autorizado';
    if (uid === 'admin' || uid === 'novio' || uid === 'novia') return 'Administrador';
    return `Administrador (${uid.substring(0, 8)}...)`;
  };

  // 1. Manejo de Activación / Desactivación en Firebase
  const handleConfirmToggle = async () => {
    const targetState = confirmModalType === 'activate';
    setConfirmModalType(null);
    setIsToggling(true);
    setErrorMessage(null);

    try {
      await updateWebMaintenanceStatusInDb(targetState, {
        title,
        message,
        returnTime,
      });

      // Update local state immediately as well
      const updatedWebMaint: WebMaintenanceSettings = {
        isEnabled: targetState,
        maintenanceEnabled: targetState,
        title,
        maintenanceTitle: title,
        message,
        maintenanceMessage: message,
        estimatedReturn: returnTime,
        maintenanceReturnTime: returnTime,
        enabledAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      onSaveSettings({
        ...settings,
        webMaintenance: updatedWebMaint,
        maintenanceEnabled: targetState,
      });
    } catch (err: any) {
      console.error('Error al cambiar modo mantenimiento:', err);
      setErrorMessage(err?.message || 'No se pudo actualizar el modo mantenimiento en Firebase.');
    } finally {
      setIsToggling(false);
    }
  };

  // 2. Guardar cambios de textos en Firebase
  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveStatus('saving');
    setErrorMessage(null);

    try {
      await saveWebMaintenanceConfigInDb({
        title,
        message,
        returnTime,
      });

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      console.error('Error al guardar configuración de mantenimiento:', err);
      setSaveStatus('error');
      setErrorMessage(err?.message || 'Error al guardar cambios en Firebase.');
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  };

  // Preview maintenance settings
  const previewMaintenanceData: WebMaintenanceSettings = {
    isEnabled: true,
    maintenanceEnabled: true,
    title: title || 'Estamos preparando algo bonito',
    maintenanceTitle: title || 'Estamos preparando algo bonito',
    message: message || 'Estamos realizando algunas mejoras para ofrecerte una mejor experiencia.',
    maintenanceMessage: message || 'Estamos realizando algunas mejoras para ofrecerte una mejor experiencia.',
    estimatedReturn: returnTime,
    maintenanceReturnTime: returnTime,
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 text-slate-200">
      {/* HEADER DE LA SECCIÓN */}
      <div className="bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 relative overflow-hidden shadow-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-bold rounded-full border border-amber-500/30 inline-flex items-center gap-1.5 mb-2">
              <Wrench className="w-3.5 h-3.5" />
              <span>Ajustes Web</span>
            </span>
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              Modo Mantenimiento
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Controla la disponibilidad pública de tu sitio en tiempo real.
            </p>
          </div>
        </div>
      </div>

      {/* ESTADO DEL SITIO Y CONTROL PRINCIPAL */}
      <div className="bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
          Estado del sitio
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-6 border-b border-white/5">
          <div className="flex items-center gap-4">
            <div
              className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 border transition-all ${
                isMaintenanceActive
                  ? 'bg-amber-500/20 border-amber-500/40 text-amber-400 shadow-lg shadow-amber-500/10'
                  : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400 shadow-lg shadow-emerald-500/10'
              }`}
            >
              {isMaintenanceActive ? (
                <Wrench className="w-6 h-6 animate-pulse" />
              ) : (
                <Power className="w-6 h-6" />
              )}
            </div>

            <div>
              <div className="flex items-center gap-2.5">
                <span
                  className={`w-3 h-3 rounded-full ${
                    isMaintenanceActive ? 'bg-amber-500 animate-ping' : 'bg-emerald-500'
                  }`}
                />
                <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                  {isMaintenanceActive ? '🟠 SITIO EN MANTENIMIENTO' : '🟢 SITIO DISPONIBLE'}
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">
                {isMaintenanceActive
                  ? 'Los visitantes ven exclusivamente la pantalla de mantenimiento.'
                  : 'La web está abierta y operativa para todos los visitantes.'}
              </p>
            </div>
          </div>

          {/* BOTÓN MAESTRO DE ACCIÓN */}
          <div className="shrink-0">
            {isMaintenanceActive ? (
              <button
                type="button"
                id="btn-desactivar-mantenimiento"
                disabled={isToggling}
                onClick={() => setConfirmModalType('deactivate')}
                className="w-full sm:w-auto px-6 py-3.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-sm rounded-2xl shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2.5 transition-all cursor-pointer active:scale-95"
              >
                {isToggling ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                <span>Desactivar mantenimiento</span>
              </button>
            ) : (
              <button
                type="button"
                id="btn-activar-mantenimiento"
                disabled={isToggling}
                onClick={() => setConfirmModalType('activate')}
                className="w-full sm:w-auto px-6 py-3.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-bold text-sm rounded-2xl shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2.5 transition-all cursor-pointer active:scale-95"
              >
                {isToggling ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Wrench className="w-4 h-4" />
                )}
                <span>Activar mantenimiento</span>
              </button>
            )}
          </div>
        </div>

        {/* FEEDBACK DE ERROR SI HUBO FALLO */}
        {errorMessage && (
          <div className="mt-4 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-xs text-red-300 flex items-center gap-3">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>

      {/* CONFIGURACIÓN DE LA PANTALLA */}
      <form
        onSubmit={handleSaveConfig}
        className="bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6"
      >
        <div className="border-b border-white/5 pb-4">
          <h2 className="text-base font-bold text-white tracking-tight">
            Configuración de la pantalla de mantenimiento
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Personaliza el mensaje que verán los visitantes mientras el sitio esté en pausa.
          </p>
        </div>

        {/* TÍTULO */}
        <div className="space-y-2">
          <label htmlFor="maint-title" className="block text-xs font-semibold text-slate-300">
            Título
          </label>
          <input
            id="maint-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Estamos preparando algo bonito"
            className="w-full px-4 py-3 bg-slate-950/60 border border-white/10 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
          />
        </div>

        {/* MENSAJE */}
        <div className="space-y-2">
          <label htmlFor="maint-message" className="block text-xs font-semibold text-slate-300">
            Mensaje
          </label>
          <textarea
            id="maint-message"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Estamos realizando algunas mejoras para ofrecerte una mejor experiencia."
            className="w-full px-4 py-3 bg-slate-950/60 border border-white/10 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors resize-none"
          />
        </div>

        {/* TIEMPO ESTIMADO (OPCIONAL) */}
        <div className="space-y-2">
          <label htmlFor="maint-return-time" className="block text-xs font-semibold text-slate-300">
            Tiempo estimado de regreso <span className="text-slate-500 font-normal">(opcional)</span>
          </label>
          <input
            id="maint-return-time"
            type="text"
            value={returnTime}
            onChange={(e) => setReturnTime(e.target.value)}
            placeholder="Volveremos muy pronto"
            className="w-full px-4 py-3 bg-slate-950/60 border border-white/10 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
          />
        </div>

        {/* BOTONES: VISTA PREVIA Y GUARDAR CAMBIOS */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="w-full sm:w-auto px-5 py-2.5 bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-95"
          >
            <Eye className="w-3.5 h-3.5 text-amber-400" />
            <span>Vista previa</span>
          </button>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {saveStatus === 'saved' && (
              <span className="text-xs font-medium text-emerald-400 flex items-center gap-1.5 animate-fade-in">
                <CheckCircle2 className="w-4 h-4" />
                <span>Guardado correctamente</span>
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="text-xs font-medium text-red-400 flex items-center gap-1.5 animate-fade-in">
                <AlertCircle className="w-4 h-4" />
                <span>Error al guardar</span>
              </span>
            )}

            <button
              type="submit"
              disabled={saveStatus === 'saving'}
              className="w-full sm:w-auto px-6 py-2.5 bg-pink-600 hover:bg-pink-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-lg shadow-pink-600/20 flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-95"
            >
              {saveStatus === 'saving' ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  <span>Guardar cambios</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* METADATOS DE AUDITORÍA */}
        <div className="pt-5 border-t border-white/5 text-[11px] text-slate-400 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            Última actualización:{' '}
            <strong className="text-slate-300 font-semibold">
              {formattedUpdateDate() || 'Pendiente de primer guardado'}
            </strong>
          </div>
          <div>
            Actualizado por:{' '}
            <strong className="text-slate-300 font-semibold">{updatedByText()}</strong>
          </div>
        </div>
      </form>

      {/* MODAL DE CONFIRMACIÓN: ACTIVAR */}
      {confirmModalType === 'activate' && (
        <ConfirmationModal
          isOpen={true}
          title="¿Activar el modo mantenimiento?"
          message="Los visitantes verán la pantalla de mantenimiento hasta que lo desactives."
          confirmText="Activar mantenimiento"
          cancelText="Cancelar"
          type="warning"
          onConfirm={handleConfirmToggle}
          onCancel={() => setConfirmModalType(null)}
        />
      )}

      {/* MODAL DE CONFIRMACIÓN: DESACTIVAR */}
      {confirmModalType === 'deactivate' && (
        <ConfirmationModal
          isOpen={true}
          title="¿Desactivar el modo mantenimiento?"
          message="El sitio volverá a estar disponible para los visitantes."
          confirmText="Desactivar"
          cancelText="Cancelar"
          type="info"
          onConfirm={handleConfirmToggle}
          onCancel={() => setConfirmModalType(null)}
        />
      )}

      {/* MODAL DE VISTA PREVIA EXACTA */}
      <AnimatePresence>
        {isPreviewOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 sm:p-6"
          >
            <div className="w-full max-w-2xl h-[90vh] max-h-[750px] bg-slate-950 border border-white/10 rounded-3xl overflow-hidden shadow-2xl flex flex-col relative">
              {/* Barra superior de la vista previa */}
              <div className="p-4 bg-slate-900 border-b border-white/10 flex items-center justify-between text-xs text-slate-300 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="font-semibold text-white">Vista Previa: Así la verán los visitantes</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="p-1.5 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Contenedor del render real */}
              <div className="flex-1 overflow-y-auto relative">
                <WebMaintenanceScreen
                  maintenance={previewMaintenanceData}
                  settings={settings}
                  isPreview={true}
                  onClosePreview={() => setIsPreviewOpen(false)}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
